import { Box } from '@cdl/heartbeat-react';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { squareToPixel } from '../../../plot/utils/graph.utils';
import { Cluster, ClusterBeat } from '../../clustering.types';
import { CanvasStrip } from '../../../plot/contexts/canvas-strip.context';
import * as beatsActions from '../../../beats/beats.actions';
import {
  clusterHorizontalLengthSelector,
  clusterVerticalRangeSelector,
} from '../../selectors/cluster.selectors';
import { BeatActionBar } from './BeatActionBar';
import { selectBeats } from '../../thunks/select-beats.thunk';
import { gainSelector } from '../../../layout/selectors/layout.selectors';
import grid from '../../../../assets/images/grid.png';
import {
  isBeatHoveredSelector,
  isBeatSelectedSelector,
  pinnedBeatSelector,
  sortedBeatsSelector,
} from '../../../beats/beats.selector';

export const STRIP_BEAT_HEIGHT = squareToPixel(2.5);
export const OFFSET = squareToPixel(0.5);
export const STRIP_BEAT_WIDTH = squareToPixel(17);
export const TICK_WIDTH = squareToPixel(1.5);
export const PADDING_RIGHT = 8;
export const BORDER_WIDTH = 1;
export const PADDING_Y = 8;
export const MARGIN_BOTTOM = 8;

interface Props {
  beat: ClusterBeat;
  cluster: Cluster;
}

export const BeatStrip: React.FC<Props> = ({ beat, cluster }) => {
  const dispatch = useDispatch();
  const isSelected = useSelector(isBeatSelectedSelector(beat.id));
  const clusterBeats = useSelector(sortedBeatsSelector);
  const pinnedBeat = useSelector(pinnedBeatSelector);
  const hovered = useSelector(isBeatHoveredSelector(beat.id));
  const xMax = useSelector(clusterHorizontalLengthSelector(cluster.id));
  const gain = useSelector(gainSelector);
  const { yMin, yMax } = useSelector(clusterVerticalRangeSelector);
  const memoisedBorderColor = useMemo(
    () => (isSelected ? 'primary.500' : 'neutral.100'),
    [isSelected],
  );
  const beats = useMemo(() => [beat], [beat]);

  const MemoisedCanvasContext = useMemo(
    () => (
      <CanvasStrip
        clusterId={cluster.id}
        beats={beats}
        width={STRIP_BEAT_WIDTH}
        height={STRIP_BEAT_HEIGHT}
        xAxis={{ xMax }}
        yAxis={{ yMin: yMin / gain, yMax: yMax / gain }}
        shadowBeats={pinnedBeat ? [pinnedBeat] : undefined}
        offsetY={OFFSET}
      >
        <img
          src={grid}
          alt='grid'
          style={{
            userSelect: 'none',
            position: 'absolute',
            width: STRIP_BEAT_WIDTH,
            height: STRIP_BEAT_HEIGHT + 2 * OFFSET,
          }}
        />
      </CanvasStrip>
    ),
    [beats, cluster.id, gain, pinnedBeat, xMax, yMax, yMin],
  );
  const selectBeat = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (clusterBeats.length > 1) {
        dispatch(
          selectBeats(beat.id, event.shiftKey, event.ctrlKey || event.metaKey),
        );
      }
    },
    [dispatch, beat.id, clusterBeats],
  );

  const onHoverLeave = useCallback(() => {
    dispatch(beatsActions.toggleHoveredBeat({}));
  }, [dispatch]);

  const onHoverEnter = useCallback(() => {
    dispatch(beatsActions.toggleHoveredBeat({ id: beat.id }));
  }, [dispatch, beat]);

  return (
    <Box
      bgColor='neutral.0'
      display='inline-block'
      padding={`${PADDING_Y}px ${PADDING_RIGHT}px ${PADDING_Y}px 0`}
      borderRadius='4px'
      marginBottom={`${MARGIN_BOTTOM}px`}
      border={`${BORDER_WIDTH}px solid`}
      borderColor={memoisedBorderColor}
      cursor={clusterBeats.length > 1 ? 'pointer' : 'default'}
      shadow={hovered && clusterBeats.length > 1 ? 400 : undefined}
      onClick={selectBeat}
      onMouseEnter={onHoverEnter}
      onMouseLeave={onHoverLeave}
    >
      <Box display='flex' width={STRIP_BEAT_WIDTH + TICK_WIDTH}>
        <BeatActionBar beat={beat} />
        {MemoisedCanvasContext}
      </Box>
    </Box>
  );
};
